import { t } from '@lingui/macro'
import { AnimatePresence, motion } from 'framer-motion'
import React, { useCallback, useMemo, useState } from 'react'
import styled, { useTheme } from 'styled-components'

import { FlexColumn } from '@/@bothrs-ui/web/components/layout'
import { P, Span } from '@/@bothrs-ui/web/components/typography/Body'
import { H2 } from '@/@bothrs-ui/web/components/typography/Heading'
import { breakpoints } from '@/config/layout'
import { ContentMasterDetailLayout } from '@/layout/components/content-master-detail-layout/ContentMasterDetailLayout'
import { getDropInProps } from '../../animations/dropIn.animation'
import { getHighlightInfo, searchSpecialisations } from '../../helpers/specialisation-search'
import { HighlightedText } from '../highlighted-text/HighlightedText'
import { AnimatedLoader } from '../loader/Loader'
import { SectionList } from '../section-list/SectionList'

import type { FC, ReactNode } from 'react'
import type { ESignSpecialisation } from '@/modules/appointment/types/e-sign.types'
import type { RankedResult } from '../../helpers/specialisation-search'
import type { SectionItem, SectionListItem } from '../section-list/SectionList'

type Props = {
  /**
   * The list of specialisations to display
   */
  specialisations: ESignSpecialisation[]

  /**
   * The list of highlighted specialisations (filtered from the list of specialisations)
   */
  highlightedSpecialisations?: number[]

  /**
   * Whether the list is loading
   */
  isLoading?: boolean

  /**
   * Whether the list has an error
   */
  hasError?: boolean

  /**
   * The error message to display
   */
  error?: string

  /**
   * The title of the list
   */
  title?: string

  /**
   * The description of the list
   */
  description: string

  /**
   * The component to display when there are no results
   */
  noResultComponent?: ReactNode

  /**
   * The function to call when an item is selected
   */
  onSelect: (item: SectionListItem<ESignSpecialisation>) => void

  /**
   * Callback when a should be auto-selected
   */
  onAutoSelectDoctor?: (doctorId: number) => void

  /**
   * Callback when search query changes
   */
  onSearchResultsChange?: (query: string, results: RankedResult[]) => void
}

/**
 * Reusable component for selecting a specialisation
 * It wraps a SectionList component and adds a title and description
 * Features:
 * - Alphabetical grouping of specialisations
 * - Highlighted specialisations section
 * - Search functionality with text highlighting
 * - Responsive layout
 */

export const SpecialisationSelection: FC<Props> = ({
  specialisations,
  highlightedSpecialisations,
  isLoading,
  title,
  description,
  noResultComponent,
  error,
  onSelect,
  onAutoSelectDoctor,
  onSearchResultsChange,
}) => {
  const [searchQuery, setSearchQuery] = useState<string>('')
  const theme = useTheme()

  const filteredSpecialisations = useMemo(() => {
    return specialisations.filter((value) => value.availableForBooking ?? true)
  }, [specialisations])

  const handleSelect = (item: SectionListItem<ESignSpecialisation>) => {
    onSelect(item)
    if (!searchQuery) return
    if (searchQuery) {
      const keywords = (item.data?.searchKeywords?.nl || []).map((kw) => ({
        type: 'keyword',
        value: kw,
        id: item.id,
      }))

      const doctors = (item.data?.doctors || []).map((doctor) => {
        const name = doctor.name
        return {
          type: 'doctor',
          value: name,
          id: doctor.id,
        }
      })
      const entries = [...keywords, ...doctors]

      // Filter entries that have any fuzzy match
      const matchingEntries = entries.filter((entry) => {
        const lowerValue = entry.value.toLowerCase()
        const lowerQuery = searchQuery.toLowerCase()
        return (
          lowerValue.includes(lowerQuery) ||
          lowerValue === lowerQuery ||
          // Check for fuzzy character matches
          [...lowerQuery].every((char) => lowerValue.includes(char))
        )
      })

      if (matchingEntries.length === 1 && matchingEntries[0].type === 'doctor') {
        onAutoSelectDoctor?.(matchingEntries[0].id)
      }
    }
  }

  const handleQueryChange = useCallback((query: string) => {
    setSearchQuery(query)
  }, [])

  const specialisationSections: SectionItem<ESignSpecialisation>[] = useMemo(() => {
    let remainingSpecialisations = specialisations
    const sections: SectionItem<ESignSpecialisation>[] = []

    // Handle highlighted specialisations
    if (highlightedSpecialisations && highlightedSpecialisations.length > 0) {
      const highlightedItems: ESignSpecialisation[] = []
      const nonHighlightedItems: ESignSpecialisation[] = []

      for (const specialisation of remainingSpecialisations) {
        if (highlightedSpecialisations.includes(specialisation.id)) {
          highlightedItems.push(specialisation)
        } else {
          nonHighlightedItems.push(specialisation)
        }
      }

      if (highlightedItems.length > 0) {
        sections.push({
          title: t({
            id: 'specialisation-selection.highlighted',
            message: 'Aanbevolen voor jou',
          }),
          items: highlightedItems
            .filter((value) => value.availableForBooking ?? false)
            .map((value) => ({
              id: value.id,
              label: value.title.nl,
              value: value.title.nl,
              description: value.description?.nl || '',
              data: value,
              noPreference: value.noPreference ?? false,
              alternativeAppointmentsExclusive: value.alternativeAppointmentsExclusive,
              info: value.info,
            }))
            .sort((a, b) => a.label.localeCompare(b.label)),
        })

        remainingSpecialisations = nonHighlightedItems
      }
    }

    // Handle alphabetical grouping
    if (remainingSpecialisations) {
      const alphabet = Array.from({ length: 26 }, (_, index) => String.fromCodePoint(65 + index))

      const alphabeticalSections = alphabet
        .map((letter) => ({
          title: letter,
          items: remainingSpecialisations
            ?.filter((value) => value.title.nl.startsWith(letter))
            .filter((value) => value.availableForBooking ?? false)
            .map((value) => ({
              id: value.id,
              label: value.title.nl,
              value: value.title.nl,
              description: value.description?.nl || '',
              data: value,
              noPreference: value.noPreference ?? false,
              alternativeAppointmentsExclusive: value.alternativeAppointmentsExclusive,
              info: value.info,
            }))
            .sort((a, b) => a.label.localeCompare(b.label)),
        }))
        .filter((section) => section.items.length > 0)

      sections.push(...alphabeticalSections)
    }

    return sections
  }, [highlightedSpecialisations, specialisations])

  const filteredSections = useMemo(() => {
    if (!searchQuery || searchQuery.length < 3) return specialisationSections

    const results = searchSpecialisations(searchQuery, filteredSpecialisations)
    onSearchResultsChange?.(searchQuery, results)

    if (results.length === 0) return []

    return [
      {
        title: t({
          id: 'specialisation-selection.search-results',
          message: 'Zoekresultaten',
        }),
        items: results.map((result) => {
          const item = result.specialisation

          const highlightedLabel = getHighlightInfo(item.title.nl, searchQuery)

          return {
            id: item.id,
            label: <HighlightedText text={highlightedLabel.text} segments={highlightedLabel.segments} />,
            value: item.title.nl,
            description: (
              <Span type='BodyLargeRegular'>
                {result.descriptionItems.map((m, index) => {
                  const highlighted = getHighlightInfo(m, searchQuery)
                  return (
                    <React.Fragment key={index}>
                      {index > 0 && ', '}
                      <HighlightedText text={highlighted.text} segments={highlighted.segments} />
                    </React.Fragment>
                  )
                })}
              </Span>
            ),
            data: item,
            noPreference: item.noPreference ?? false,
            alternativeAppointmentsExclusive: item.alternativeAppointmentsExclusive,
            info: item.info,
          }
        }),
      },
    ]
  }, [specialisationSections, filteredSpecialisations, searchQuery, onSearchResultsChange])

  return (
    <ContentMasterDetailLayout>
      <ContentMasterDetailLayout.Master>
        <FlexColumn gap={theme.UI.Spacing.Space2}>
          {title ? <StTitle>{title}</StTitle> : <StDescription type='BodyLargeRegular'>{description}</StDescription>}
          {error && <StError type='BodyMediumRegular'>{error}</StError>}
        </FlexColumn>
      </ContentMasterDetailLayout.Master>

      <ContentMasterDetailLayout.Detail>
        <AnimatePresence mode='wait' initial={false}>
          {isLoading ? (
            <motion.div key='loader' {...getDropInProps({ exit: { opacity: 0, y: -30 } })}>
              <AnimatedLoader />
            </motion.div>
          ) : (
            <motion.div key='list' {...getDropInProps()}>
              <SectionList<ESignSpecialisation>
                sections={filteredSections}
                noResultComponent={noResultComponent}
                onQueryChange={handleQueryChange}
                onItemSelect={handleSelect}
              />
              <div style={{ height: '100px' }} />
            </motion.div>
          )}
        </AnimatePresence>
      </ContentMasterDetailLayout.Detail>
    </ContentMasterDetailLayout>
  )
}

const StTitle = styled(H2)`
  margin-bottom: 0;
`

const StDescription = styled(P)`
  font-weight: 400;
  @media ${breakpoints.m} {
    ${({ theme }) => theme.Typography.BodyLargeRegular}
  }
`

const StError = styled(P)`
  color: ${({ theme }) => theme.Colors.Status.Error};
`
